import { PRICE_ID_TO_PLAN } from "../const/planConst";
import type { ProfileModel } from "../types/models";

// "pricing-pro-59" experiment: Pro's monthly price for new signups. Control
// keeps $79; the "pro-59" arm sees and pays $59 (yearly: the same 50% off,
// $29/mo). Basic and Pro+ never change. klap-api reads the same flag when a
// checkout, plan switch or upgrade preview targets Pro
// (helpers/pro_price_experiment.ts there), so the price charged is the price
// shown. Delete with the experiment.

export type ProPriceVariant = "control" | "pro-59";

export const PRO_PRICE_FLAG_KEY = "pricing-pro-59";

// Must equal klap-api's PRO_PRICE_EXPERIMENT_LAUNCH_DATE and the flag's
// signed_up_at release condition in PostHog.
export const PRO_PRICE_EXPERIMENT_LAUNCH_DATE = new Date(
  "2026-10-05T17:45:00Z",
);

export const PRO_MONTHLY_PRICE: Record<ProPriceVariant, number> = {
  control: 79,
  "pro-59": 59,
};

// Non-guest profiles created on/after the launch date, free or paying: a
// signup who buys Basic keeps their arm's Pro price when upgrading. Older
// users and logged-out /pricing visitors never evaluate the flag and see $79.
export const isProPriceExperimentEligible = (
  profile?: Pick<ProfileModel, "isGuest" | "createdAt"> | null,
): boolean =>
  !!profile &&
  !profile.isGuest &&
  !!profile.createdAt &&
  new Date(profile.createdAt) >= PRO_PRICE_EXPERIMENT_LAUNCH_DATE;

// Anything but the "pro-59" arm (control, not enrolled, flags not loaded)
// sees today's $79.
export const proPriceVariantFor = (
  variant: string | boolean | undefined | null,
): ProPriceVariant => (variant === "pro-59" ? "pro-59" : "control");

// The $59 Pro prices: monthly, yearly checkout, yearly switch and the
// Checkout upsell (klap-api's PRO_59_* maps in billingConst.ts). Every other
// Pro price in PRICE_ID_TO_PLAN is a $79 one.
export const PRO_59_PRICE_IDS: ReadonlySet<string> = new Set([
  "price_1UNC9cJ9Xk7InalcvPR6ZqKs",
  "price_1UNC9dJ9Xk7Inalc5urU7h9t",
  "price_1UNC9dJ9Xk7Inalct4Nn4cvL",
  "price_1UNCWQJ9Xk7Inalc13w2O3aa",
]);

export type SubscriptionPriceLike = {
  plan?: string | null;
  price?: {
    id?: string | null;
    recurring?: { usage_type?: string | null } | null;
  } | null;
};

const isProPlan = (plan: string | null | undefined): boolean =>
  plan === "pro" || plan === "pro-yearly";

// Whether the account pays one of the $79 Pro prices, so it keeps seeing
// $79 whatever the flag says: since the flag serves pro-59 to every eligible
// account (Nov 4 2026), the control-arm subscribers who bought Pro at $79
// resolve to pro-59 too, and klap-api keeps them on the $79 prices
// (switchesToPro59). `subscriptions` is GET /billing/subscription's list;
// while it is still loading (undefined) a Pro profile is taken to pay $79, so
// a $79 subscriber never sees $59 flash. Free users and $59 subscribers are
// unaffected.
export const keepsPro79Price = ({
  plan,
  subscriptions,
}: {
  plan?: string | null;
  subscriptions?: SubscriptionPriceLike[] | null;
}): boolean => {
  if (!subscriptions) return isProPlan(plan);
  const current = subscriptions.find(
    (subscription) => subscription.price?.recurring?.usage_type !== "metered",
  );
  if (!current) return false;
  const priceId = current.price?.id ?? "";
  const currentPlan = current.plan ?? PRICE_ID_TO_PLAN.get(priceId);
  return isProPlan(currentPlan) && !PRO_59_PRICE_IDS.has(priceId);
};
